<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

import BuildingRenderCanvasAccessible from '@/components/maps_canvases/BuildingRenderCanvasAccessible.vue';
import ImageLightbox from '@/components/utilities/ImageLightbox.vue';
import { getUnitSummaryPlanImageUrl } from '@/helpers/imageHelpers.ts';
import type { LocaleSchema } from '@/locales/index.ts';
import type { BuildingFacade } from '@/typings/buildings/BuildingFacade';
import type { FacadeRenderLayer } from '@/typings/drawing/FacadeRenderLayer';
import type { Unit } from '@/typings/units/Unit.ts';
import ArrowLeft from '@/components/svg/ArrowLeft.vue';
import ArrowRight from '@/components/svg/ArrowRight.vue';

const { t } = useI18n<{message: LocaleSchema}>({
  useScope: 'global',
});

const props = defineProps<{
  unit: Unit;
  buildingPlanImageUrl: string;
  selectedConfigurationId?: string;
  buildingFacades?: BuildingFacade[];
}>();

const currentPlanIndex = ref(0);
const currentFacadeIndex = ref(0);

// Variables pour le swipe tactile
const touchStartX = ref(0);
const touchEndX = ref(0);
const isTouch = ref(false);
const minSwipeDistance = 50;
const carouselContainer = ref<HTMLElement>();

const facadesWithUnit = computed(() => {
  if (!props.buildingFacades || props.buildingFacades.length === 0) {
    return [];
  }
  
  const sortedFacades = [...props.buildingFacades].sort((a, b) => a.facadeOrder - b.facadeOrder);
  
  // Première facade (par ordre)
  const firstFacade = sortedFacades[0];
  
  // Facades avec polygones ET qui contiennent l'unité
  const facadesWithPolygons = sortedFacades.filter(facade =>
    facade.facadeUnits.some(facadeUnit =>
      facadeUnit.unitId === props.unit.id &&
      facadeUnit.canvasPolygonData &&
      facadeUnit.canvasPolygonData.trim() !== ''
    )
  );
  
  // Vérifier si la première facade contient l'unité (avec ou sans polygones)
  const firstFacadeHasUnit = firstFacade && firstFacade.facadeUnits.some(fu => fu.unitId === props.unit.id);
  
  // Créer un Set pour éviter les doublons
  const result = new Set();
  
  // Ajouter les facades avec polygones
  facadesWithPolygons.forEach(facade => result.add(facade));
  
  // Ajouter la première facade si elle contient l'unité (même sans polygones)
  if (firstFacadeHasUnit) {
    result.add(firstFacade);
  }
  
  // Retourner les facades triées par ordre
  return Array.from(result).sort((a, b) => a.facadeOrder - b.facadeOrder);
});

const facadeWithUnit = computed(() => {
  return facadesWithUnit.value[currentFacadeIndex.value] || undefined;
});

const unitFacadeLayer = computed<FacadeRenderLayer | null>(() => {
  const facade = facadeWithUnit.value;
  if (!facade) return null;
  
  const facadeUnit = facade.facadeUnits.find(fu => fu.unitId === props.unit.id);
  if (!facadeUnit?.canvasPolygonData) return null;
  
  try {
    const polygons = JSON.parse(facadeUnit.canvasPolygonData);
    return {
      id: props.unit.id,
      unitId: props.unit.id,
      unitNumber: props.unit.unitNumber,
      floorName: props.unit.floorNo,
      status: props.unit.status,
      basePrice: props.unit.basePrice,
      bedrooms: props.unit.bedrooms,
      bathrooms: props.unit.bathrooms,
      dens: props.unit.dens,
      polygons: polygons
    } as FacadeRenderLayer;
  } catch (error) {
    console.error('Error parsing polygon data for unit:', props.unit.id, error);
    return null;
  }
});

const buildingImage = computed(() => {
  const facade = facadeWithUnit.value;
  if (facade?.buildingImageUrl) {
    return getUnitSummaryPlanImageUrl(facade.buildingImageUrl);
  }
  return props.unit.buildingImageUrl ? getUnitSummaryPlanImageUrl(props.unit.buildingImageUrl) : undefined;
});

const buildingPlanImage = computed(() => {
  return props.buildingPlanImageUrl ? getUnitSummaryPlanImageUrl(props.buildingPlanImageUrl) : undefined;
});

const floorPlanImage = computed(() => {
  const url = getFloorPlanUrl();
  return url ? getUnitSummaryPlanImageUrl(url) : undefined;
});

const floorPlanModalUrl = computed(() => {
  return getFloorPlanUrl();
});

function getFloorPlanUrl(): string | undefined {
  if (props.selectedConfigurationId && props.unit.unitUnitConfigurations) {
    const configId = parseInt(props.selectedConfigurationId);
    const configImage = props.unit.unitUnitConfigurations.find(
      ci => ci.unitConfigurationId === configId
    );
    return configImage?.planImageUrl;
  }
  
  return undefined;
}

const carouselImages = computed(() => {
  const images = [];

  if (buildingPlanImage.value) {
    images.push({
      url: buildingPlanImage.value,
      originalUrl: props.buildingPlanImageUrl,
      alt: t('unitSummary.buildingPlan'),
      type: 'buildingPlan'
    });
  }
  
  return images;
});

const totalPlans = computed(() => {
  return Math.max(carouselImages.value.length, 1);
});

const currentImage = computed(() => {
  return carouselImages.value[currentPlanIndex.value];
});

function previousPlan() {
  if (currentPlanIndex.value > 0) {
    currentPlanIndex.value--;
  }
}

function nextPlan() {
  if (currentPlanIndex.value < totalPlans.value - 1) {
    currentPlanIndex.value++;
  }
}

function previousFacade() {
  if (currentFacadeIndex.value > 0) {
    currentFacadeIndex.value--;
  }
}

function nextFacade() {
  if (currentFacadeIndex.value < facadesWithUnit.value.length - 1) {
    currentFacadeIndex.value++;
  }
}

// Fonctions de gestion du swipe tactile
function handleTouchStart(event: TouchEvent) {
  if (facadesWithUnit.value.length <= 1) return;
  
  isTouch.value = true;
  touchStartX.value = event.touches[0].clientX;
}

function handleTouchMove(event: TouchEvent) {
  if (!isTouch.value || facadesWithUnit.value.length <= 1) return;
  
  // Empêcher le scroll vertical pendant le swipe horizontal
  const currentX = event.touches[0].clientX;
  const deltaX = Math.abs(currentX - touchStartX.value);
  
  // Si le mouvement horizontal est significatif, empêcher le scroll vertical
  if (deltaX > 10) {
    event.preventDefault();
  }
}

function handleTouchEnd(event: TouchEvent) {
  if (!isTouch.value || facadesWithUnit.value.length <= 1) return;
  
  touchEndX.value = event.changedTouches[0].clientX;
  const deltaX = touchStartX.value - touchEndX.value;
  
  if (Math.abs(deltaX) > minSwipeDistance) {
    if (deltaX > 0) {
      // Swipe vers la gauche - facade suivante
      nextFacade();
    } else {
      // Swipe vers la droite - facade précédente
      previousFacade();
    }
  }
  
  isTouch.value = false;
}

// Lifecycle hooks pour les event listeners tactiles
onMounted(() => {
  if (carouselContainer.value) {
    carouselContainer.value.addEventListener('touchstart', handleTouchStart, { passive: false });
    carouselContainer.value.addEventListener('touchmove', handleTouchMove, { passive: false });
    carouselContainer.value.addEventListener('touchend', handleTouchEnd, { passive: true });
  }
});

onUnmounted(() => {
  if (carouselContainer.value) {
    carouselContainer.value.removeEventListener('touchstart', handleTouchStart);
    carouselContainer.value.removeEventListener('touchmove', handleTouchMove);
    carouselContainer.value.removeEventListener('touchend', handleTouchEnd);
  }
});
</script>

<template>
  <section class="unitSummary-unitAndBuildingPlan">
    <figure v-if="floorPlanImage"
            class="unitSummary-plan">
      <div class="unitSummary-planFrame">
        <ImageLightbox :display-url="floorPlanImage"
                       :modal-url="floorPlanModalUrl"
                       class="unitSummary-planImage" />
      </div>
      <figcaption id="unitPlanFigCaption">{{ $t('unitSummary.unitPlan') }}</figcaption>
    </figure>

    <div v-else
         class="unitSummary-planPlaceholder">
      {{ t('unitSummary.upcomingUnitPlan') }}
    </div>

    <div v-if="facadeWithUnit" class="unitSummary-planViewer">
      <div ref="carouselContainer" class="unitSummary-carouselWrapper">
        <div class="unitSummary-planContainer">
          <div class="unitSummary-planDisplay">
            <div class="imageCarousel-imageContainer">
              <BuildingRenderCanvasAccessible
                :layers="unitFacadeLayer ? [unitFacadeLayer] : []"
                :image-url="facadeWithUnit.buildingImageUrl"
                :hide-onboarding-tooltip="true"
                class="unitSummary-facadeCanvas">
                <template #tooltip="{ layer }">
                  <div v-if="layer && layer.unitNumber">
                    {{ `${t('browsingApp.units.unitNumber')} ${layer.unitNumber}` }}
                  </div>
                </template>
              </BuildingRenderCanvasAccessible>
            </div>
          </div>
        </div>
        
        <button v-if="facadesWithUnit.length > 1"
                class="imageCarousel-arrow imageCarousel-arrow--left"
                :disabled="currentFacadeIndex === 0"
                @click="previousFacade">
          <ArrowLeft class="imageCarousel-arrowIcon" />
        </button>
        
        <button v-if="facadesWithUnit.length > 1"
                class="imageCarousel-arrow imageCarousel-arrow--right"
                :disabled="currentFacadeIndex === facadesWithUnit.length - 1"
                @click="nextFacade">
          <ArrowRight class="imageCarousel-arrowIcon" />
        </button>
      </div>
      
      <div v-if="facadesWithUnit.length > 1" class="imageCarousel-dots">
        <button v-for="(facade, index) in facadesWithUnit"
                :key="facade.id"
                class="imageCarousel-dot"
                :class="{ 'imageCarousel-dot--active': index === currentFacadeIndex }"
                @click="currentFacadeIndex = index">
        </button>
      </div>
    </div>
    <div v-else-if="carouselImages.length > 0" class="unitSummary-planViewer">
      <div class="unitSummary-carouselWrapper">
        <div class="unitSummary-planContainer">
          <div class="unitSummary-planDisplay">
            <figure v-if="currentImage" class="unitSummary-plan">
              <ImageLightbox :display-url="currentImage.url"
                            :modal-url="currentImage.originalUrl"
                            class="unitSummary-planImage" />
            </figure>
            
            <div v-else class="unitSummary-planPlaceholder">
              {{ t('unitSummary.upcomingBuildingPlan') }}
            </div>
          </div>
        </div>
        
        <button v-if="carouselImages.length > 1"
                class="unitSummary-navButton unitSummary-navButton_prev"
                :disabled="currentPlanIndex === 0"
                @click="previousPlan">
          <ArrowLeft class="arrow-icon" />
        </button>
        
        <button v-if="carouselImages.length > 1"
                class="unitSummary-navButton unitSummary-navButton_next"
                :disabled="currentPlanIndex === totalPlans - 1"
                @click="nextPlan">
          <ArrowRight class="arrow-icon" />
        </button>
      </div>
      
      <div v-if="totalPlans > 1" class="unitSummary-planIndicators">
        <span v-for="n in totalPlans"
              :key="n"
              class="unitSummary-planIndicator"
              :class="{ 'unitSummary-planIndicator_active': n - 1 === currentPlanIndex }"
              @click="currentPlanIndex = n - 1">
        </span>
      </div>
    </div>
    <div v-else
         class="unitSummary-planPlaceholder">
      {{ t('unitSummary.upcomingBuildingPlan') }}
    </div>
  </section>
</template>

<style lang="scss" scoped>
.unitSummary-unitAndBuildingPlan {
  --flowSpacing: var(--spc24-16);
  
  > * + * {
    margin-block-start: var(--flowSpacing);
  }
}

.unitSummary-planViewer {
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
  width: 100%;
}

.unitSummary-carouselWrapper {
  position: relative;
  width: 100%;
  background-color: #fff;
  border: var(--contentSizeBorderWidth) solid var(--contentColorBorder, var(--gray-200));
  border-radius: var(--radius-md);
  overflow: hidden;
}

.unitSummary-planContainer {
  display: flex;
  align-items: center;
  overflow: hidden;
}

.unitSummary-planDisplay {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.imageCarousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid var(--gray-200);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background: rgba(255, 255, 255, 1);
    transform: translateY(-50%) scale(1.05);
    border-color: var(--primary-400);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &--left {
    left: 12px;
  }
  
  &--right {
    right: 12px;
  }
}

.imageCarousel-arrowIcon {
  width: 20px;
  height: 20px;
  color: var(--gray-600);
  
  .imageCarousel-arrow:hover & {
    color: var(--primary-600);
  }
}

.imageCarousel-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 1rem;
}

.imageCarousel-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: var(--gray-300);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--gray-400);
    transform: scale(1.1);
  }
  
  &--active {
    background: var(--primary-500);
    
    &:hover {
      background: var(--primary-600);
    }
  }
}

.unitSummary-plan {
  figcaption {
    margin-block-start: 8px;
    font-weight: bold;
  }
}

.unitSummary-planFrame {
  background-color: white;
  border: var(--contentSizeBorderWidth) solid var(--contentColorBorder, var(--gray-200));
  border-radius: var(--radius-md);
  overflow: hidden;
  &_noPadding {
    padding: 0;
  }
}

.unitSummary-planImage {
  display: block;
  max-width: 100%;
  height: auto;
  min-height: 200px;
  max-height: 400px;
  margin-inline: auto;

  overflow: clip;

  &[data-image-is-loading="true"] {
    width: 100%;
  }
}

.unitSummary-planPlaceholder {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  font-size: var(--fs20-14);
  font-weight: 700;
  border: var(--contentSizeBorderWidth) solid var(--contentColorBorder, var(--gray-200));
  border-radius: var(--radius-md);
}

.imageCarousel-imageContainer {
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 0;
  background: var(--gray-100);
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.unitSummary-buildingPlanWithCanvas {
  width: 100%;
  min-height: 120px;
}

.unitSummary-facadeCanvas {
  width: 100%;
  height: auto;
}

@media (width <= 600px) {
  .unitSummary-floorPlan {
    aspect-ratio: 16/9;
  }
  
  .unitSummary-buildingPlan {
    display: none;
  }
}
</style>

<style lang="scss">
.renderCanvas_facade {
  --baseColor: var(--gray-100);
}

.renderCanvas_facade [data-item-status="Available"] {
  --baseColor: var(--clrGreenStatus);
}

.renderCanvas_facade [data-item-status="Reserved"] {
  --baseColor: var(--clrOrangeStatus);
}

.renderCanvas_facade [data-item-status="Sold"] {
  --baseColor: var(--clrRedStatus);
}

.renderCanvas_facade [data-item-status="Rented"] {
  --baseColor: var(--clrRedStatus);
}

.renderCanvas_facade [data-item-status="Upcoming"] {
  --baseColor: var(--clrYellowStatus);
}
</style>

